import React, { useCallback } from "react";
import styled from "styled-components";

import { useIntl } from "../../../../../shared/core/i18n/use-intl";
import { BluePrimaryButton, WhitePrimaryButton } from "../../../common/buttons/primary-button";
import { Modal } from "../../../common/modal/modal";
import { WhiteModalContainer } from "../../../common/modal/white-modal-container";
import { theme } from "../../../styles/theme";

interface RemoveConfirmModalProps {
  modalId: string;
  /** Prefix of the deletePrompt block holding the title, message and button labels. */
  labels: string;
  onConfirm: () => void;
}

/** Asks before removing an attachment or a sub-expense: the two prompts read the same. */
export function RemoveConfirmModal({ modalId, labels, onConfirm }: Readonly<RemoveConfirmModalProps>) {
  const { formatMessage } = useIntl();
  const closeModal = useCallback(() => Modal.dismiss(modalId), [modalId]);

  return (
    <WhiteModalContainer id={modalId}>
      <Title>{formatMessage(`${labels}.title`)}</Title>
      <Message>{formatMessage(`${labels}.message`)}</Message>
      <Buttons>
        <ConfirmButton size="XS" onClick={onConfirm}>
          {formatMessage(`${labels}.confirmButton`)}
        </ConfirmButton>
        <CancelButton size="XS" onClick={closeModal}>
          {formatMessage(`${labels}.cancelButton`)}
        </CancelButton>
      </Buttons>
    </WhiteModalContainer>
  );
}

const Title = styled.h2`
  ${theme.mediumText};
  margin: 0;
  font-size: 20px;
  line-height: 26px;
  color: ${theme.colors.blue[800]};
`;

const Message = styled.p`
  ${theme.text};
  margin: 8px 0 0;
  font-size: 14px;
  line-height: 19px;
  color: ${theme.colors.gray[800]};
`;

const Buttons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 24px;
`;

const ConfirmButton = styled(BluePrimaryButton)`
  width: 100%;
  padding: 13px;
  box-shadow: none;
`;

/* Outlined, not a plain white fill: DefaultButton clears the border, so it is set back here. */
const CancelButton = styled(WhitePrimaryButton)`
  width: 100%;
  padding: 13px;
  border: 1px solid ${theme.colors.blue[700]};
  box-shadow: none;
  color: ${theme.colors.blue[700]};

  &:focus,
  &:active,
  &:hover {
    background-color: ${theme.colors.gray[50]};
  }
`;
